import React, { useState } from "react";

import { animated, useSpring } from "@react-spring/web";
import RocketIcon from "@uui/Rocket02";

import { Typography, Box } from "@mui/material";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";

import FypImage from "@app/analytics/assets/fyp_page_demo.png";
import FypTopicsImage from "@app/analytics/assets/fyp_page_topics_demo.png";
import useTimeout from "@app/common/hooks/useTimeout";
import ROUTES from "@app/common/routes";
import type { BillingCurrentPlan } from "@app/common/types";
import usePageTitle from "@app/utils/hooks/usePageTitle";

import { NewBadge } from "@rui/FeatureBadge";
import Link from "@rui/Link";
import { Column, Row } from "@rui/Stack";
import Stepper from "@rui/Stepper";

const AnimatedImage = animated(Box<"img">);

const STEPS = [
  {
    title: "For You",
    description:
      "Get a 360-degree view of topics over time across your meetings, updated daily. Now available for all paid Read plans.",
    image: FypImage,
    isNew: true,
    width: "100%",
  },
  {
    title: "Daily Summary",
    description:
      "Read automatically generates a high-level summary to recap what you've been working on, and also surfaces themes across your tags and Workspace.",
    image: FypImage,
    width: "180%",
    top: 20,
    left: 30,
  },
  {
    title: "Topics & What You Missed",
    description:
      "Quickly identify the themes of your work and the collaborators involved. View the main topics across your meetings, as well as topics you may have missed.",
    image: FypImage,
    width: "180%",
    top: -420,
    left: 30,
  },
  {
    title: "Daily Read",
    description:
      "Your Daily Read podcast offers a concise overview of the past 24 hours and prepares you for upcoming meetings. Perfect for catching up on your daily commute.",
    image: FypImage,
    width: "200%",
    top: 20,
    left: -700,
  },
  {
    title: "Get a closer look",
    description:
      "Expand any topic to get a deep dive. View a topic summary, relevant clips from your meetings, action items with statuses, and a Q&A",
    image: FypTopicsImage,
  },
];

const PulseUpsell = ({ currentPlan }: { currentPlan: BillingCurrentPlan }) => {
  usePageTitle("For You - Upgrade");

  const [step, setStep] = useState(0);

  const handleNextStep = () => {
    setStep((state) => (state + 1) % STEPS.length);
  };

  const handleClickStep = (i: number) => {
    setStep(i);
  };

  useTimeout(handleNextStep, 8000, true, [step]);

  const currentStep = STEPS[step];

  const imgStyle = useSpring({
    to: {
      top: currentStep.top ?? 0,
      left: currentStep.left ?? 0,
      width: currentStep.width ?? "100%",
    },
  });

  return (
    <Column
      variant="center"
      sx={{ height: "100%", padding: "24px" }}
      data-test-id="pulse-upsell"
    >
      <Paper
        elevation={8}
        sx={{
          border: "1px solid rbg(0,0,0,0.08)",
          overflow: "hidden",
          width: "100%",
          maxWidth: "1067px",
          maxHeight: "545px",
        }}
      >
        <Row variant="apart" alignItems="stretch" maxHeight="545px">
          <Column
            variant="left"
            maxWidth="33%"
            minWidth="308px"
            justifyContent="center"
            padding="24px 40px"
          >
            <Column variant="left" spacing="24px">
              <Column spacing={1}>
                <Box height="28px">{currentStep.isNew && <NewBadge />}</Box>
                <Typography variant="h2">{currentStep.title}</Typography>
                <Typography
                  variant="bodyPrimaryReg"
                  lineHeight={1.5}
                  minHeight="200px"
                >
                  {currentStep.description}
                </Typography>
              </Column>
              <Row variant="left" spacing="12px">
                <Stepper
                  activeStepColor="shades.primary.500"
                  activeStep={step}
                  onClickStep={handleClickStep}
                  steps={5}
                />
                <Button
                  variant="outlined"
                  sx={{ maxHeight: "32px" }}
                  onClick={handleNextStep}
                >
                  Next
                </Button>
              </Row>
              <Column
                variant="left"
                spacing="12px"
                sx={{
                  backgroundColor: "shades.ghost.300",
                  padding: "8px",
                  borderRadius: "6px",
                }}
              >
                <Typography variant="labelReg" color="shades.neutral.500">
                  {currentPlan.label && (
                    <>
                      You're on the{" "}
                      <Link to={ROUTES.BILLING} underline>
                        {currentPlan.label} plan.
                      </Link>{" "}
                    </>
                  )}
                  Upgrade to Read Pro or Enterprise to access For You insights,
                  unlimited meeting reports, and more.
                </Typography>
                <Button
                  startIcon={<RocketIcon />}
                  component={Link}
                  to={ROUTES.BILLING_COMPARE_PLANS}
                  sx={{
                    background: (theme) => theme.palette.gradients.read,
                    height: "44px",
                    color: "white",
                    padding: "12px 16px",
                  }}
                >
                  Upgrade now
                </Button>
              </Column>
            </Column>
          </Column>
          <Box
            maxWidth="66%"
            width="100%"
            minWidth="598px"
            sx={{
              background: (theme) => theme.palette.gradients.light,
              padding: "30px",
              overflow: "hidden",
            }}
          >
            <AnimatedImage
              component="img"
              src={currentStep.image}
              sx={{
                width: currentStep.width ?? "100%",
                borderRadius: "4px",
                minWidth: "641px",
                position: "relative",
              }}
              style={imgStyle}
            />
          </Box>
        </Row>
      </Paper>
    </Column>
  );
};

export default PulseUpsell;
